import * as React from 'react';
import { memo } from 'react';
import { Row, useTable } from 'react-table';
import { useTranslation } from 'react-i18next';

import styled, { css } from 'styled-components';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconsNames,
  IconButton,
  Icon,
  Title,
} from 'atoms';

import { LoadingData, LoadingTable, InfiniteScrollContainer } from 'molecules';

import { IconSizes, Gradients } from 'components/types';
import { Colors } from 'services/ColorService';

type Accessors =
  | 'applicationNumber'
  | 'fullName'
  | 'groupNumber'
  | 'lessonsCount'
  | 'teacherType'
  | 'price';

export type IRow = {
  [key in Accessors]: React.ReactNode;
} & {
  id: string;
  isTerminated: boolean;
  isCompleted: boolean;
};

export interface IColumn {
  accessor: Accessors;
  flexible?: boolean;
  align?: 'left' | 'right' | 'center';
  Header?: string | React.ReactElement;
  columns?: IColumn[];
  marginRight?: string;
  marginLeft?: string;
  columnWidth?: string;
}

export interface IApplicationTableComponent {
  columns: IColumn[];
  rows: IRow[];
  isFlexible: boolean;
  noDataText: string;
  loadMore: () => void;
  hasMore: boolean;
  onEditButtonClick: (applicationId: string) => void;
  loading?: boolean;
}

export const ApplicationsTableComponent = memo(
  ({
    isFlexible,
    noDataText,
    rows: rowsSource,
    columns,
    loadMore,
    hasMore,
    onEditButtonClick,
    loading,
  }: IApplicationTableComponent) => {
    const { t } = useTranslation();
    const {
      getTableProps,
      headers,
      getTableBodyProps,
      rows,
      prepareRow,
    } = useTable({
      columns,
      data: rowsSource,
    });

    return (
      <Wrapper>
        <InfiniteScrollContainer
          loadMore={loadMore}
          hasMore={hasMore}
          dataLength={rows.length}
          loading={loading || false}
        >
          <Table {...getTableProps()} flexible={isFlexible} rowShadowOnHover>
            <TableHead>
              <TableRow type="row">
                {headers.map((column: any) => {
                  const {
                    key: cellKey,
                    ...restCellProps
                  } = column.getHeaderProps({
                    type: 'headerCell',
                    flexible: column.flexible && isFlexible,
                    width: column.columnWidth,
                    margin: {
                      right: column.marginRight,
                      left: column.marginLeft,
                    },
                  });

                  return (
                    <TableCell key={cellKey} {...restCellProps}>
                      {column.render('Header')}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody {...getTableBodyProps()}>
              {!rows.length && !loading && (
                <TableRow key="noData">
                  <TableCell type="cell" width="100%" align="center">
                    {noDataText}
                  </TableCell>
                </TableRow>
              )}
              {!rows.length && loading && (
                <TableRow>
                  <TableCell width="100%" align="center">
                    <LoadingTable />
                  </TableCell>
                </TableRow>
              )}
              {Boolean(rows.length) &&
                rows.map((row: Row<IRow>) => {
                  prepareRow(row);
                  const { key: rowKey, ...restRowProps } = row.getRowProps();

                  return (
                    <ApplicationTableRow
                      key={rowKey}
                      type="row"
                      isTerminated={row.original.isTerminated}
                      isCompleted={row.original.isCompleted}
                      {...restRowProps}
                    >
                      {row.cells.map((cell: any) => {
                        const {
                          key: cellKey,
                          ...restCellProps
                        } = cell.getCellProps({
                          width: cell.column.columnWidth,
                          flexible: cell.column.flexible && isFlexible,
                          margin: {
                            right: cell.column.marginRight,
                            left: cell.column.marginLeft,
                          },
                        });

                        const blueText =
                          cell.column.Header ===
                            `${t('table.applications.number')}` ||
                          cell.column.Header ===
                            `${t('table.applications.group')}`;

                        if (
                          cell.column.id === 'applicationNumber' &&
                          row.original.isTerminated
                        ) {
                          return (
                            <TableCell
                              key={cellKey}
                              textColor={blueText ? Colors.Blue : Colors.Black}
                              {...restCellProps}
                            >
                              <UsernameWrapper>
                                {cell.render('Cell')}
                                <IconTerminatedWrapper>
                                  <IconTerminated name={IconsNames.Error} />
                                  <TitleStyled
                                    text={t('table.applications.isTerminated')}
                                  />
                                </IconTerminatedWrapper>
                              </UsernameWrapper>
                            </TableCell>
                          );
                        }

                        if (
                          cell.column.id === 'applicationNumber' &&
                          row.original.isCompleted
                        ) {
                          return (
                            <TableCell
                              key={cellKey}
                              textColor={blueText ? Colors.Blue : Colors.Black}
                              {...restCellProps}
                            >
                              <UsernameWrapper>
                                {cell.render('Cell')}
                                <IconCompletedWrapper>
                                  <IconCompleted name={IconsNames.Completed} />
                                  <TitleStyled
                                    text={t('table.applications.isCompleted')}
                                  />
                                </IconCompletedWrapper>
                              </UsernameWrapper>
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell
                            key={cellKey}
                            textColor={blueText ? Colors.Blue : Colors.Black}
                            {...restCellProps}
                          >
                            {cell.render('Cell')}
                          </TableCell>
                        );
                      })}

                      <RowButtons>
                        <IconButtonStyled
                          size={IconSizes.lg}
                          name={IconsNames.Edit}
                          onClick={() => onEditButtonClick(row.original.id)}
                        />
                      </RowButtons>
                    </ApplicationTableRow>
                  );
                })}
            </TableBody>
          </Table>
        </InfiniteScrollContainer>
        {Boolean(rows.length) && loading && <LoadingData />}
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  width: 100%;
  overflow-y: hidden;
  overflow-x: auto;
`;

const ApplicationTableRow = styled((props) => (
  <TableRow {...props}>{props.children}</TableRow>
))`
  position: relative;
  ${({ isTerminated, isCompleted }) => {
    if (isTerminated) {
      return css`
        background: ${Gradients.InactiveRed};
      `;
    }
    if (isCompleted) {
      return css`
        background: #e7eeff;
      `;
    }
  }}
`;

const UsernameWrapper = styled.span`
  position: relative;
  width: auto;
`;

const TitleStyled = styled((props) => <Title {...props} />)`
  display: none;
`;

const IconTerminatedWrapper = styled.div`
  position: absolute;
  top: 0;
  left: calc(100% + 16px);
  height: 16px;
  width: 16px;
  cursor: pointer;

  &:hover {
    ${TitleStyled} {
      display: block;
    }
  }
`;

const IconCompletedWrapper = styled.div`
  position: absolute;
  top: 0;
  left: calc(100% + 16px);
  height: 16px;
  width: 16px;
  cursor: pointer;

  &:hover {
    ${TitleStyled} {
      display: block;
    }
  }
`;

const IconTerminated = styled(Icon)`
  height: 100%;
  width: 100%;
`;

const IconCompleted = styled(Icon)`
  height: 100%;
  width: 100%;
`;

const RowButtons = styled.td`
  position: absolute;
  top: 12px;
  right: 24px;
  bottom: 12px;
  display: none;
  align-items: center;
  ${ApplicationTableRow}:hover & {
    display: flex;
  }
`;

const IconButtonStyled = styled((props) => <IconButton {...props} />)`
  display: flex;
  height: 32px;
  width: 32px;
  border-radius: 100%;

  :hover {
    & svg {
      & > path {
        fill: ${Colors.White};
      }
      & > circle {
        fill: ${Colors.Blue};
      }
    }
  }
`;
